<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="lead-create">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #actions>
          <ButtonComponent
            :label="$t('create')"
            theme="primary"
            type="submit"
            :disabled="!validator.passes"
            :icon="['fal', 'plus']"
            :promise="promises.mutate"
            @click="submit"
          />
        </template>
      </HeaderComponent>
      <Card tag="form" @submit.prevent="submit" class="lead-create__form">
        <template #header>
          <FontAwesomeIcon :icon="['fal', 'address-card']" size="6x" />
        </template>
        <template #default>
          <div class="lead-create__inputs">
            <InputComponent
              v-model="data.contact.name"
              data-field="contact.name"
              :label="labels['contact.name']"
              required
              :is-valid="validator.fields['contact.name'].valid"
              :validation-message="validator.fields['contact.name'].error"
            />

            <InputComponent
              v-model="data.contact.email"
              data-field="contact.email"
              :label="labels['contact.email']"
              type="email"
              required
              :is-valid="validator.fields['contact.email'].valid"
              :validation-message="validator.fields['contact.email'].error"
              :icon="['fal', 'envelope']"
            />

            <InputComponent
              v-model="data.contact.phone"
              data-field="contact.phone"
              :label="labels['contact.phone']"
              type="tel"
              required
              :is-valid="validator.fields['contact.phone'].valid"
              :validation-message="validator.fields['contact.phone'].error"
              :icon="['fal', 'mobile']"
            />

            <Tags
              data-field="contact.tags"
              :label="$tc('tag', 2)"
              v-model="data.contact.tags"
              :placeholder="$tc('add')"
              autocomplete="/api/contact/tags"
            />

            <CheckboxComponent v-model="isCompany" :label="$t('company')" />

            <Company
              v-if="data.contact.company"
              v-model="data.contact.company"
              :validator="validator"
              validator-prefix="contact.company"
            />

            <Address
              v-model="data.contact.address"
              :show-map="false"
              data-field="contact.address"
              validator-prefix="contact.address"
              :validator="validator"
              class="my-2"
            />

            <template v-if="onModel === 'Project'">
              <CheckboxComponent v-model="data.contact.upsell" :label="labels['contact.upsell']" />

              <InputComponent :label="$t('leadReferences')" data-field="references">
                <template #default>
                  <ChecklistComponent
                    v-model="data.scopes"
                    :items="fetchResidentials"
                    :compare="(a, b) => a.ref === b.ref"
                    class="elder-input__element"
                  >
                    <template #default>
                      <CheckboxComponent
                        v-model="data.scopes"
                        :items="fetchResidentials"
                        :state="fullReference"
                        :compare="(a, b) => a.ref === b.ref"
                        :label="$tc('theWholeProject', 1)"
                      />
                      <hr />
                    </template>
                  </ChecklistComponent>
                </template>
              </InputComponent>
            </template>
          </div>
        </template>
      </Card>
    </div>
  </Loader>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import ChecklistComponent from '@/components/Select/Checklist'
import Validator from '@/mixins/validator'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import Model from '@/models/Lead/Create'
import ResidentialApi from '@/api/Residential'
import Company from '@/components/Company'
import Tags from '@/components/Tags'
import Address from '@/components/Address'
import CompanyModel from '@/models/Company'

export default {
  mixins: [Validator],
  props: {
    reference: String,
  },
  data() {
    return {
      data: Model(),
      validator: {
        rules: 'rules',
        labels: 'labels',
        data: 'data',
      },
    }
  },
  computed: {
    ...mapState('Lead', ['item', 'promises']),
    ...mapi18n('labels', {
      'contact.email': 'email',
      'contact.name': 'name',
      'contact.phone': 'phone',
      'contact.upsell': 'contactUpsell',
      'contact.address.street': 'street',
      'contact.address.city': 'city',
      'contact.address.postcode': 'postcode',
      'contact.address.county': 'county',
      'contact.company.name': 'companyName',
      'contact.company.organizationNumber': 'orgNo',
    }),

    rules() {
      let base = {
        'contact.name': 'required',
        'contact.email': 'required|email',
        'contact.phone': 'required|mobile',
        'contact.address.street': 'string',
        'contact.address.city': 'string',
        'contact.address.postcode': 'string',
        'contact.address.county': 'string',
      }

      if (Boolean(this.data.contact.company))
        return {
          ...base,
          'contact.company.name': 'required',
          'contact.company.organizationNumber': 'required',
        }

      return base
    },

    onModel() {
      return this.$route.meta.onModel || 'Project'
    },
    breadcrumbs() {
      const context = this.onModel.toLowerCase()

      return [
        {
          title: (JSON.parse(sessionStorage.getItem(`${context}:category`)) || {}).label || this.$tc(context, 2),
          action: { name: `${context}s` },
        },
        {
          title: this.$path('item.reference.name'),
          action: {
            name: `${context}-dashboard`,
            params: { id: this.$path('item.reference.id') },
          },
        },
        {
          title: this.$tc('lead', 2),
          action: {
            name: `${context}-leads`,
            params: { id: this.$path('item.reference.id') },
          },
        },
        { title: this.$metaInfo.title },
      ]
    },

    isCompany: {
      get() {
        return Boolean(this.data.contact.company)
      },
      set(val) {
        this.$set(this.data.contact, 'company', val ? CompanyModel() : null)
      },
    },

    fullReference() {
      return {
        ref: this.reference,
        onModel: this.onModel,
      }
    },
  },
  methods: {
    ...mapActions('Lead', ['fetch', 'mutate']),
    submit() {
      let data = { ...this.data, reference: this.fullReference }
      this.mutate(data).then(data => {
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: `${this.onModel.toLowerCase()}-lead-edit`,
          params: { id: data.id, reference: this.reference },
        })
      })
    },
    fetchResidentials() {
      return ResidentialApi.get(
        { filter: { project: this.reference }, sort: { key: 'number', value: 1 } },
        { query: 'ResidentialSearch' },
      ).then(data =>
        data.map(e => ({
          value: { ref: e.id, onModel: 'Residential' },
          label: `${this.$tc('residential')} ${e.number}`,
        })),
      )
    },
  },

  created() {
    return this.fetch([
      { reference: this.reference },
      {
        query: `${this.onModel}LeadCreate`,
        transform: v => ({ reference: v[this.onModel] }),
      },
    ])
  },
  metaInfo() {
    return {
      title: this.$t('createLead'),
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    CheckboxComponent,
    ChecklistComponent,
    Card,
    Company,
    Tags,
    Address,
  },
}
</script>

<style lang="scss">
.lead-create {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 550px;
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  [type='submit'] {
    margin-right: auto;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 250px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }
}
</style>
